import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import {
  Fuel,
  Route,
  Truck,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  MapPin,
  ClipboardCheck,
  FileSignature,
  Wrench,
} from "lucide-react";

const MODE_BG = {
  cash: "bg-emerald-100 text-emerald-700",
  imprest: "bg-bravura-100 text-bravura-700",
  qr: "bg-sky-100 text-sky-700",
};

export default function DriverHome() {
  const [fuelSummary, setFuelSummary] = useState(null);
  const [tripSummary, setTripSummary] = useState(null);
  const [pendingHO, setPendingHO] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [f, t, h] = await Promise.allSettled([
          api.get("/fuel/me/summary"),
          api.get("/logistics/trips/by-driver/summary"),
          api.get("/vehicle-ops/handovers/driver/my"),
        ]);
        if (f.status === "fulfilled") setFuelSummary(f.value.data);
        if (t.status === "fulfilled") setTripSummary(t.value.data);
        if (h.status === "fulfilled") {
          const pending = (h.value.data || []).filter(
            (r) => r.status === "pending_driver_ack",
          ).length;
          setPendingHO(pending);
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const driver = fuelSummary?.driver || tripSummary?.driver;
  const lastTruck = fuelSummary?.last_truck;

  return (
    <div className="space-y-6" data-testid="driver-home">
      {/* Greeting hero */}
      <div className="rounded-2xl bg-gradient-to-br from-bravura via-bravura-700 to-bravura-800 text-white p-5 shadow-lg">
        <div className="text-[11px] uppercase tracking-widest text-bravura-100">Welcome back</div>
        <div className="text-2xl font-bold mt-0.5" style={{ fontFamily: "Chivo" }}>
          {driver?.name || "Driver"}
        </div>
        <div className="text-xs text-bravura-100 mt-1 flex flex-wrap items-center gap-2">
          {driver?.code && <span>Code: {driver.code}</span>}
          {lastTruck?.truck_no && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15">
              <Truck className="h-3 w-3" /> {lastTruck.truck_no}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <div className="rounded-xl bg-white/10 backdrop-blur p-3">
            <div className="text-[10px] uppercase tracking-widest text-bravura-100">Refills</div>
            <div className="text-2xl font-bold" style={{ fontFamily: "Chivo" }}>
              {fuelSummary?.total_refills ?? "—"}
            </div>
          </div>
          <div className="rounded-xl bg-white/10 backdrop-blur p-3">
            <div className="text-[10px] uppercase tracking-widest text-bravura-100">Trips</div>
            <div className="text-2xl font-bold" style={{ fontFamily: "Chivo" }}>
              {tripSummary?.total_trips ?? "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Two big action buttons */}
      <div className="grid grid-cols-1 gap-3">
        <Link
          to="/driver/fuel/new"
          data-testid="driver-action-refill"
          className="block rounded-2xl bg-slate-900 text-white p-5 shadow-lg active:scale-[0.99] transition"
        >
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-bravura-500 flex items-center justify-center shadow-inner">
              <Fuel className="h-7 w-7 text-slate-900" />
            </div>
            <div className="flex-1">
              <div className="text-lg font-bold">Diesel Refill</div>
              <div className="text-xs text-slate-300">Diesel / Urea + bill photos + UPI QR</div>
            </div>
            <ChevronRight className="h-6 w-6 text-slate-400" />
          </div>
        </Link>

        <Link
          to="/driver/trips/new"
          data-testid="driver-action-trip"
          className="block rounded-2xl bg-slate-900 text-white p-5 shadow-lg active:scale-[0.99] transition"
        >
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-sky-500 flex items-center justify-center shadow-inner">
              <Route className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-lg font-bold">Log a Trip</div>
              <div className="text-xs text-slate-300">Destination + RTO/Tolls/Loading + POD</div>
            </div>
            <ChevronRight className="h-6 w-6 text-slate-400" />
          </div>
        </Link>

        <Link
          to="/driver/inspection/new"
          data-testid="driver-action-inspection"
          className="block rounded-2xl bg-slate-900 text-white p-5 shadow-lg active:scale-[0.99] transition"
        >
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-inner">
              <ClipboardCheck className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-lg font-bold">Vehicle Inspection</div>
              <div className="text-xs text-slate-300">Pre-trip / Post-trip / Weekly safety check</div>
            </div>
            <ChevronRight className="h-6 w-6 text-slate-400" />
          </div>
        </Link>

        <Link
          to="/driver/maintenance/new"
          data-testid="driver-action-maintenance"
          className="block rounded-2xl bg-slate-900 text-white p-5 shadow-lg active:scale-[0.99] transition"
        >
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-red-500 flex items-center justify-center shadow-inner">
              <Wrench className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-lg font-bold">Report Maintenance</div>
              <div className="text-xs text-slate-300">Breakdown / Repair / Service report admin ko bhejo</div>
            </div>
            <ChevronRight className="h-6 w-6 text-slate-400" />
          </div>
        </Link>

        <Link
          to="/driver/handovers"
          data-testid="driver-action-handover"
          className="block rounded-2xl bg-slate-900 text-white p-5 shadow-lg active:scale-[0.99] transition relative"
        >
          {pendingHO > 0 && (
            <span
              className="absolute top-3 right-3 bg-red-500 text-white text-[10px] font-bold rounded-full h-6 min-w-[24px] px-1.5 flex items-center justify-center"
              data-testid="driver-ho-badge"
            >
              {pendingHO}
            </span>
          )}
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-amber-500 flex items-center justify-center shadow-inner">
              <FileSignature className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-lg font-bold">Vehicle Handovers</div>
              <div className="text-xs text-slate-300">
                {pendingHO > 0
                  ? `${pendingHO} pending — review & e-sign`
                  : "Review past handovers"}
              </div>
            </div>
            <ChevronRight className="h-6 w-6 text-slate-400" />
          </div>
        </Link>
      </div>

      {/* Recent activity */}
      <div className="space-y-4">
        {(fuelSummary?.recent_refills?.length ?? 0) > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                <Fuel className="h-3.5 w-3.5" /> Recent Refills
              </div>
              <Link to="/driver/refills" className="text-xs text-bravura font-medium">
                See all →
              </Link>
            </div>
            <div className="space-y-2">
              {fuelSummary.recent_refills.slice(0, 3).map((r) => (
                <Link
                  key={r.id}
                  to={`/driver/refills/${r.id}`}
                  className="block rounded-xl bg-white ring-1 ring-slate-200 p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-bravura-100 flex items-center justify-center">
                      <Fuel className="h-4 w-4 text-bravura-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold truncate">{r.truck_no}</div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${MODE_BG[r.payment_mode]}`}>
                          {r.payment_mode.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {r.refill_date} · {r.diesel_litres} L · {formatINR(r.total_amount)}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {(tripSummary?.recent?.length ?? 0) > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                <Route className="h-3.5 w-3.5" /> Recent Trips
              </div>
              <Link to="/driver/trips" className="text-xs text-sky-600 font-medium">
                See all →
              </Link>
            </div>
            <div className="space-y-2">
              {tripSummary.recent.slice(0, 3).map((t) => (
                <Link
                  key={t.id}
                  to={`/driver/trips/${t.id}`}
                  className="block rounded-xl bg-white ring-1 ring-slate-200 p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-sky-100 flex items-center justify-center">
                      <Truck className="h-4 w-4 text-sky-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold truncate">{t.truck_no}</div>
                        {t.status === "settled" ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-bravura-100 text-bravura-700">
                            <AlertCircle className="h-3 w-3" /> Open
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {t.location_name} · {formatINR(t.actual_total)}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {!loading && (fuelSummary?.recent_refills?.length ?? 0) === 0 && (tripSummary?.recent?.length ?? 0) === 0 && (
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-6 text-center text-sm text-slate-500">
            No activity yet. Tap a button above to start.
          </div>
        )}
      </div>
    </div>
  );
}
